import { useState } from 'react';

interface PreviewFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PreviewFormModal({ isOpen, onClose }: PreviewFormModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      data.append(key, value as string);
    });
    try {
      await fetch('https://readdy.ai/api/form/d72ln3smc4tbhfnc6hdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0d1117] to-[#0d2420] px-8 py-7">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line" style={{ fontSize: '20px' }}></i>
          </button>
          <p className="text-emerald-400 text-xs font-bold tracking-widest uppercase mb-2">Free Preview</p>
          <h3 className="text-white text-xl md:text-2xl font-black leading-tight">
            See What Your Website Could Look Like
          </h3>
          <p className="text-white/50 text-sm mt-1">No cost. No obligation. Preview ready in 48 hours.</p>
        </div>

        {/* Body */}
        <div className="px-8 py-7">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-50 border border-emerald-100 mx-auto mb-5">
                <i className="ri-checkbox-circle-fill text-emerald-500" style={{ fontSize: '32px' }}></i>
              </div>
              <h4 className="text-[#0d1117] font-bold text-xl mb-2">Request Received!</h4>
              <p className="text-[#6b7280] text-sm leading-relaxed">
                We&apos;ll prepare your free website preview and reach out within 48 hours. Keep an eye on your inbox.
              </p>
              <button
                onClick={onClose}
                className="whitespace-nowrap mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold text-sm px-6 py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              id="preview-request-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#374151] text-xs font-semibold mb-1.5">Full Name *</label>
                  <input
                    name="full_name"
                    type="text"
                    required
                    placeholder="John Smith"
                    className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3 text-sm text-[#0d1117] placeholder-[#d1d5db] focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[#374151] text-xs font-semibold mb-1.5">Business Name *</label>
                  <input
                    name="business_name"
                    type="text"
                    required
                    placeholder="Your Business LLC"
                    className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3 text-sm text-[#0d1117] placeholder-[#d1d5db] focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#374151] text-xs font-semibold mb-1.5">Email Address *</label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@yourbusiness.com"
                  className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3 text-sm text-[#0d1117] placeholder-[#d1d5db] focus:outline-none focus:border-emerald-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-[#374151] text-xs font-semibold mb-1.5">Phone Number *</label>
                <input
                  name="phone"
                  type="tel"
                  required
                  placeholder="(555) 000-0000"
                  className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3 text-sm text-[#0d1117] placeholder-[#d1d5db] focus:outline-none focus:border-emerald-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-[#374151] text-xs font-semibold mb-1.5">Current Website URL</label>
                <input
                  name="website_url"
                  type="text"
                  placeholder="www.yourbusiness.com"
                  className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3 text-sm text-[#0d1117] placeholder-[#d1d5db] focus:outline-none focus:border-emerald-400 transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm py-4 rounded-xl hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-60"
              >
                {submitting ? 'Sending Request...' : 'Request My Free Website Preview →'}
              </button>

              <p className="text-[#9ca3af] text-xs text-center">
                No credit card. No commitment. Preview delivered within 48 hours.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
